import { Box, Typography } from "@mui/material";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { FC, ReactNode } from "react";

export type TagVariant = "outlined" | "solid";
export type TagSize = "s" | "m";
export type TagColor = "grey" | "success" | "error" | "info" | "warning" | "primary";

type Props = {
  title: string;
  titleSecondary?: string;
  tooltip?: string;
  onClick?: () => void;
  variant?: TagVariant;
  color?: TagColor;
  icon?: ReactNode;
  after?: ReactNode;
  size?: TagSize;
};

type VariantStyleType = {
  color: string;
  backgroundColor: string;
  borderColor: string | null;
};

const variantStyleMap: Record<TagColor, Record<TagVariant, VariantStyleType>> = {
  success: {
    outlined: {
      color: "textPrimary",
      backgroundColor: "white",
      borderColor: "success",
    },
    solid: {
      color: "success",
      backgroundColor: "success.light",
      borderColor: null,
    },
  },
  error: {
    outlined: {
      color: "error",
      backgroundColor: "white",
      borderColor: "error",
    },
    solid: {
      color: "error",
      backgroundColor: "error.light",
      borderColor: null,
    },
  },
  grey: {
    outlined: {
      color: "textPrimary",
      backgroundColor: "white",
      borderColor: "grey.300",
    },
    solid: {
      color: "textPrimary",
      backgroundColor: "grey.100",
      borderColor: null,
    },
  },
  info: {
    outlined: {
      color: "info.main",
      backgroundColor: "white",
      borderColor: "info.main",
    },
    solid: {
      color: "info.main",
      backgroundColor: "info.light",
      borderColor: null,
    },
  },
  warning: {
    outlined: {
      color: "warning.main",
      backgroundColor: "white",
      borderColor: "warning.main",
    },
    solid: {
      color: "warning.dark",
      backgroundColor: "warning.light",
      borderColor: null,
    },
  },
  primary: {
    outlined: {
      color: "primary.main",
      backgroundColor: "primary.light",
      borderColor: "primary.main",
    },
    solid: {
      color: "primary.dark",
      backgroundColor: "primary.light",
      borderColor: null,
    },
  },
};

type SizeStyleType = {
  height: string;
  titleVariant: "caption" | "body2";
  titleFontWeight: number;
};

const sizeStyleMap: Record<TagSize, SizeStyleType> = {
  s: {
    height: "25px",
    titleVariant: "caption",
    titleFontWeight: 500,
  },
  m: {
    height: "32px",
    titleVariant: "caption",
    titleFontWeight: 700,
  },
};

const getVariantStyle = (variant: TagVariant, color: TagColor): VariantStyleType => {
  return variantStyleMap[color][variant];
};

export const Tag: FC<Props> = ({
  title,
  titleSecondary,
  onClick,
  variant = "outlined",
  color = "grey",
  icon,
  tooltip,
  after,
  size = "s",
}) => {
  const variantStyles = getVariantStyle(variant, color);
  const sizeStyles = sizeStyleMap[size];

  return (
    <BlTooltip title={tooltip ?? undefined}>
      <Flex
        alignItems="center"
        flexDirection="row"
        gap={Spacing.M}
        sx={{
          height: sizeStyles.height,
          borderRadius: Radius.XL,
          border: variantStyles.borderColor ? "1px solid" : "none",
          ...(variantStyles.borderColor && { borderColor: variantStyles.borderColor }),
          backgroundColor: variantStyles.backgroundColor,
          py: Spacing.S,
          px: Spacing.L,
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
      >
        {icon && <Flex color={variantStyles.color}>{icon}</Flex>}
        <HStack spacing={Spacing.S} alignItems={"center"}>
          <Typography
            variant={sizeStyles.titleVariant}
            color={variantStyles.color}
            fontWeight={sizeStyles.titleFontWeight}
            lineHeight={1.4}
          >
            {title}
          </Typography>
          {titleSecondary && (
            <Typography variant="caption" color={variantStyles.color} fontWeight={300} lineHeight={1.4}>
              {titleSecondary}
            </Typography>
          )}
          {after && <Box onClick={(e) => e.stopPropagation()}>{after}</Box>}
        </HStack>
      </Flex>
    </BlTooltip>
  );
};
